import { useEffect, useMemo, useState, type FormEvent } from 'react';

import type {
    AdminProfessionalServiceRow,
    AdminUserEditCompanyOption,
} from '../../../services/api';
import {
    useAdminProfessionalServiceMutations,
    useAdminUserEditOptions,
} from '../../../hooks/useApi';
import { getMutationErrorMessage } from '../../../lib/apiErrors';
import { useTouchedFields } from '../../../lib/touchedFormValidation';
import { Button } from '../../ui/button';
import { ConfirmDialog } from '../../ui/confirm-dialog';
import { Input } from '../../ui/input';
import { Select } from '../../ui/select';
import { Actions, DialogFrame, Field, FormErrorAlert } from './creditDialogPrimitives';
import { MANUAL_LEDGER_LINKED_ARTIFACT_TYPE_OPTIONS } from './adminCreditAdapters';

interface ProfessionalServiceDialogProps {
    open: boolean;
    professionalService: AdminProfessionalServiceRow | null;
    onClose: () => void;
    onSaved?: (mode: 'create' | 'edit') => void;
}

interface LinkageDraft {
    linkedArtifactType: string;
    linkedArtifactId: string;
}

function buildInitialLinkages(
    professionalService: AdminProfessionalServiceRow | null,
): LinkageDraft[] {
    if (!professionalService || professionalService.linkages.length === 0) {
        return [];
    }

    return professionalService.linkages.map((linkage) => ({
        linkedArtifactType: linkage.linkedArtifactType,
        linkedArtifactId: linkage.linkedArtifactId,
    }));
}

function getValidationErrors(name: string, linkages: LinkageDraft[]) {
    const errors: Record<string, string> = {};
    if (!name.trim()) {
        errors.name = 'Name is required.';
    }
    linkages.forEach((linkage, index) => {
        const hasType = linkage.linkedArtifactType.trim().length > 0;
        const hasId = linkage.linkedArtifactId.trim().length > 0;
        if (!hasType && !hasId) {
            return;
        }
        if (!hasType) {
            errors[`linkedArtifactType-${index}`] = 'Linked artifact type is required.';
        }
        if (!hasId) {
            errors[`linkedArtifactId-${index}`] = 'Linked artifact ID is required.';
        }
    });
    return errors;
}

function getProfessionalServiceFieldNames(linkages: LinkageDraft[]): string[] {
    const fields = ['name'];
    linkages.forEach((_, index) => {
        fields.push(`linkedArtifactType-${index}`, `linkedArtifactId-${index}`);
    });
    return fields;
}

function normalizeLinkageDrafts(linkages: LinkageDraft[]): LinkageDraft[] {
    return linkages
        .map((linkage) => ({
            linkedArtifactType: linkage.linkedArtifactType.trim(),
            linkedArtifactId: linkage.linkedArtifactId.trim(),
        }))
        .filter((linkage) => linkage.linkedArtifactType && linkage.linkedArtifactId);
}

function buildTeamSelectOptions(
    companies: AdminUserEditCompanyOption[],
    professionalService: AdminProfessionalServiceRow | null,
) {
    const options: Array<{ value: string; label: string; searchText: string }> = [];
    const seenTeamIds = new Set<string>();

    for (const company of companies) {
        for (const team of company.teams) {
            const value = String(team.teamId);
            if (seenTeamIds.has(value)) {
                continue;
            }
            seenTeamIds.add(value);
            options.push({
                value,
                label: `${team.teamName} (#${team.teamId})`,
                searchText: `${team.teamName} ${team.teamId} ${company.companyName}`,
            });
        }
    }

    if (professionalService?.teamId && !seenTeamIds.has(String(professionalService.teamId))) {
        options.unshift({
            value: String(professionalService.teamId),
            label: `${professionalService.teamName ?? professionalService.teamId} (#${professionalService.teamId})`,
            searchText: `${professionalService.teamName ?? ''} ${professionalService.teamId}`,
        });
    }

    options.sort((left, right) => left.label.localeCompare(right.label));

    return [{ value: '', label: 'No team', searchText: 'no team' }, ...options];
}

export function ProfessionalServiceDialog({
    open,
    professionalService,
    onClose,
    onSaved,
}: ProfessionalServiceDialogProps) {
    const { createProfessionalService, updateProfessionalService } =
        useAdminProfessionalServiceMutations();
    const { touch, touchAll, resetTouched, fieldError } = useTouchedFields<string>();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedTeamId, setSelectedTeamId] = useState('');
    const [linkages, setLinkages] = useState<LinkageDraft[]>([]);
    const [error, setError] = useState<string | null>(null);

    const teamOptionsQuery = useAdminUserEditOptions(undefined, 100, open);
    const teamCompanies = useMemo(
        () => teamOptionsQuery.data?.pages.flatMap((page) => page.items) ?? [],
        [teamOptionsQuery.data?.pages],
    );

    useEffect(() => {
        if (!open) return;
        setName(professionalService?.name ?? '');
        setDescription(professionalService?.description ?? '');
        setSelectedTeamId(professionalService?.teamId ? String(professionalService.teamId) : '');
        setLinkages(buildInitialLinkages(professionalService));
        setError(null);
        resetTouched();
    }, [open, professionalService, resetTouched]);

    const fieldErrors = useMemo(() => getValidationErrors(name, linkages), [linkages, name]);

    const teamSelectOptions = useMemo(
        () => buildTeamSelectOptions(teamCompanies, professionalService),
        [professionalService, teamCompanies],
    );

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        touchAll(getProfessionalServiceFieldNames(linkages));
        if (Object.keys(fieldErrors).length > 0) {
            return;
        }

        const request = {
            name: name.trim(),
            description: description.trim() || null,
            sowId: professionalService?.sowId ?? null,
            teamId: selectedTeamId ? Number(selectedTeamId) : null,
            linkages: normalizeLinkageDrafts(linkages),
        };

        try {
            if (professionalService) {
                await updateProfessionalService.mutateAsync({
                    professionalServiceId: professionalService.id,
                    request,
                });
                onSaved?.('edit');
            } else {
                await createProfessionalService.mutateAsync(request);
                onSaved?.('create');
            }
            onClose();
        } catch (err) {
            setError(
                getMutationErrorMessage(
                    err,
                    professionalService
                        ? 'Could not update professional service. Try again.'
                        : 'Could not create professional service. Try again.',
                ),
            );
        }
    };

    return (
        <DialogFrame
            open={open}
            onClose={onClose}
            title={
                professionalService ? 'Edit professional service' : 'Create professional service'
            }
            description="Manually record incubating professional services work and its linked artifacts."
            panelClassName="max-w-3xl"
        >
            <form className="space-y-5" onSubmit={handleSubmit}>
                {error ? <FormErrorAlert message={error} /> : null}
                <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Name" required error={fieldError('name', fieldErrors.name)}>
                        <Input
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            onBlur={() => touch('name')}
                        />
                    </Field>
                    <Field label="Description">
                        <Input
                            value={description}
                            onChange={(event) => setDescription(event.target.value)}
                        />
                    </Field>
                </div>
                <Field label="Team">
                    <Select
                        ariaLabel="Team"
                        searchable
                        searchAriaLabel="Team search"
                        searchPlaceholder="Search team name or ID"
                        options={teamSelectOptions}
                        value={selectedTeamId}
                        onValueChange={setSelectedTeamId}
                    />
                </Field>
                <div className="space-y-3 rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <p className="text-sm font-semibold text-neutral-950">
                                Linked artifacts
                            </p>
                            <p className="mt-1 text-xs text-neutral-600">
                                Optional. Add linked artifacts when the work is tied to specific
                                deliverables.
                            </p>
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() =>
                                setLinkages((current) => [
                                    ...current,
                                    { linkedArtifactType: '', linkedArtifactId: '' },
                                ])
                            }
                        >
                            Add linked artifact
                        </Button>
                    </div>
                    {linkages.map((linkage, index) => (
                        <div
                            key={`${index}-${professionalService?.id ?? 'new'}`}
                            className="space-y-3"
                        >
                            <div className="flex items-center justify-between gap-3">
                                <p className="text-sm font-medium text-neutral-900">
                                    Linked artifact {index + 1}
                                </p>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() =>
                                        setLinkages((current) =>
                                            current.filter(
                                                (_, currentIndex) => currentIndex !== index,
                                            ),
                                        )
                                    }
                                >
                                    Remove
                                </Button>
                            </div>
                            <div className="grid gap-4 md:grid-cols-2">
                                <Field
                                    label={`Linked artifact type ${index + 1}`}
                                    error={fieldError(
                                        `linkedArtifactType-${index}`,
                                        fieldErrors[`linkedArtifactType-${index}`],
                                    )}
                                >
                                    <Select
                                        ariaLabel={`Linked artifact type ${index + 1}`}
                                        options={[
                                            { value: '', label: 'Select linked artifact type' },
                                            ...MANUAL_LEDGER_LINKED_ARTIFACT_TYPE_OPTIONS,
                                        ]}
                                        value={linkage.linkedArtifactType}
                                        onValueChange={(value) => {
                                            setLinkages((current) =>
                                                current.map((entry, currentIndex) =>
                                                    currentIndex === index
                                                        ? { ...entry, linkedArtifactType: value }
                                                        : entry,
                                                ),
                                            );
                                            touch(`linkedArtifactType-${index}`);
                                        }}
                                        onBlur={() => {
                                            touch(`linkedArtifactType-${index}`);
                                        }}
                                    />
                                </Field>
                                <Field
                                    label={`Linked artifact ID ${index + 1}`}
                                    error={fieldError(
                                        `linkedArtifactId-${index}`,
                                        fieldErrors[`linkedArtifactId-${index}`],
                                    )}
                                >
                                    <Input
                                        aria-label={`Linked artifact ID ${index + 1}`}
                                        value={linkage.linkedArtifactId}
                                        onChange={(event) =>
                                            setLinkages((current) =>
                                                current.map((entry, currentIndex) =>
                                                    currentIndex === index
                                                        ? {
                                                              ...entry,
                                                              linkedArtifactId: event.target.value,
                                                          }
                                                        : entry,
                                                ),
                                            )
                                        }
                                        onBlur={() => {
                                            touch(`linkedArtifactId-${index}`);
                                        }}
                                    />
                                </Field>
                            </div>
                        </div>
                    ))}
                </div>
                <Actions onCancel={onClose}>
                    <Button
                        type="submit"
                        disabled={
                            createProfessionalService.isPending ||
                            updateProfessionalService.isPending
                        }
                    >
                        {professionalService
                            ? 'Save professional service'
                            : 'Create professional service'}
                    </Button>
                </Actions>
            </form>
        </DialogFrame>
    );
}

export function ProfessionalServiceDeleteConfirmDialog({
    open,
    professionalServiceName,
    professionalServiceId,
    onClose,
    onDeleted,
}: {
    open: boolean;
    professionalServiceName: string | null;
    professionalServiceId: string | null;
    onClose: () => void;
    onDeleted?: () => void;
}) {
    const { deactivateProfessionalService } = useAdminProfessionalServiceMutations();
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setError(null);
        }
    }, [open]);

    const handleDelete = async () => {
        if (!professionalServiceId) return;
        try {
            await deactivateProfessionalService.mutateAsync(professionalServiceId);
            onDeleted?.();
            onClose();
        } catch (err) {
            setError(
                getMutationErrorMessage(err, 'Could not delete professional service. Try again.'),
            );
        }
    };

    return (
        <ConfirmDialog
            open={open}
            title="Delete professional service?"
            description={
                professionalServiceName
                    ? `This will soft delete ${professionalServiceName}.`
                    : 'This will soft delete the selected professional service.'
            }
            confirmLabel="Delete professional service"
            pendingLabel="Deleting…"
            destructive
            isPending={deactivateProfessionalService.isPending}
            error={error}
            onConfirm={handleDelete}
            onCancel={onClose}
        >
            The record and its linked artifacts will remain in the database with a deleted timestamp
            for auditing.
        </ConfirmDialog>
    );
}
